// spectral.ts — Spectral-class metadata: surface colour, relative weight in the
// stellar population, the light colour a star of that class casts, and a core
// radius range. Colours follow the rough blackbody run O(blue) → M(red).
import type { SpectralClass } from '../types';

interface SpectralInfo {
  weight: number;
  core: string;
  light: string;
  radius: [number, number];
  /** Relative luminosity (G≈1), compressed from the real main-sequence spread so
   *  derived planet temperatures stay believable across the orbit range. Drives
   *  the climate model only (see scene/planet/model.ts); not used in generation. */
  lum: number;
}

export const SPECTRAL: Record<SpectralClass, SpectralInfo> = {
  O: { weight: 1, core: '#9bb4ff', light: '#aac4ff', radius: [0.9, 1.4], lum: 50 },
  B: { weight: 2, core: '#aecbff', light: '#cdddff', radius: [0.8, 1.2], lum: 18 },
  A: { weight: 6, core: '#dce6ff', light: '#eef3ff', radius: [0.6, 0.9], lum: 6 },
  F: { weight: 10, core: '#f8f7e8', light: '#fdf6e3', radius: [0.5, 0.75], lum: 2.2 },
  G: { weight: 16, core: '#ffe9a8', light: '#ffd98a', radius: [0.45, 0.7], lum: 1 },
  K: { weight: 26, core: '#ffc46b', light: '#ffb15a', radius: [0.4, 0.65], lum: 0.42 },
  M: { weight: 39, core: '#ff7a52', light: '#ff6a45', radius: [0.35, 0.6], lum: 0.06 },
};

const CLASSES = Object.keys(SPECTRAL) as SpectralClass[];

export function pickSpectralClass(roll: number): SpectralClass {
  const total = CLASSES.reduce((sum, c) => sum + SPECTRAL[c].weight, 0);
  let t = roll * total;
  for (const c of CLASSES) {
    t -= SPECTRAL[c].weight;
    if (t <= 0) return c;
  }
  return 'M';
}
